import type {QueryFunctionContext, UseQueryOptions} from '@tanstack/react-query'

import type {AccountInformation} from '@/auth/model'
import type {SupportedLocales} from '@/lib/locale/locale'
import type {BookmarkedProduct} from '@/models/productModel'
import {hashProduct} from '@/models/productModel'
import {getBookmarks} from '@/services/bookmarkService'
import {NO_REFETCH_OPTIONS} from '@/utils/query/constants'

export type BookmarkData = Map<string, BookmarkedProduct>

export function getBookmarkQueryKey(params: {
  locale: SupportedLocales
  accountInformation: AccountInformation | undefined
}) {
  return ['bookmark', params.locale, params.accountInformation?.id] as const
}

export function getBookmarkQueryOptions<TData = BookmarkData, TError = Error>(
  params: {
    locale: SupportedLocales
    accountInformation: AccountInformation | undefined
  },
  options?: Omit<UseQueryOptions<BookmarkData, TError, TData>, 'queryKey' | 'queryFn'>,
) {
  return queryOptions({
    queryKey: getBookmarkQueryKey(params),
    queryFn: async ({signal}: QueryFunctionContext) => {
      const bookmarks = await getBookmarks({locale: params.locale, signal})
      const bookmarkMap = new Map<string, BookmarkedProduct>()
      bookmarks.forEach(bookmark => {
        bookmarkMap.set(hashProduct(bookmark), bookmark)
      })
      return bookmarkMap
    },
    placeholderData: keepPreviousData,
    ...NO_REFETCH_OPTIONS,
    ...options,
  })
}

// Observe query - only subscribe to data, don't fetch
export function getBookmarkObserveQueryOptions<TData = BookmarkData, TError = Error>(
  params: {
    locale: SupportedLocales
    accountInformation: AccountInformation | undefined
  },
  options?: Omit<UseQueryOptions<BookmarkData, TError, TData>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return queryOptions({
    queryKey: getBookmarkQueryKey(params),
    enabled: false,
    queryFn: skipToken,
    placeholderData: keepPreviousData,
    ...NO_REFETCH_OPTIONS,
    ...options,
  })
}

export default function useBookmarkQuery(
  locale: SupportedLocales,
  accountInformation: AccountInformation | undefined,
) {
  return useQuery(
    getBookmarkQueryOptions({
      locale,
      accountInformation,
    }),
  )
}
